import { AlertTriangle, CheckCircle2, ListChecks, ShieldAlert, Square } from "lucide-react";

const WARN = /CRITICAL|WARNING|DANGER|NEVER|INVISIBLE|HAZARD|DANGEROUS|NON-NEGOTIABLE|EXTREME CARE|FRAGILE|STOP|NO SAFE/;
const STEPS = /CHECKLIST|PROTOCOL|PROCEDURE|RESPONSE|PHASE|DURING OPERATION|POST-OPERATION|PRE-OPERATION|EVERY SESSION/;
const isHead = (t) => t.length < 90 && /[A-Z]/.test(t) && t === t.toUpperCase() && !/^\d+\.\s/.test(t) && !/^[•·□☐-]/.test(t);

// Splits plain lesson text into blocks: { head, kind, lines }.
export const parseLesson = (text) => {
  const blocks = [{ head: null, kind: "plain", lines: [] }];
  for (const raw of (text || "").split("\n")) {
    const t = raw.trim();
    if (!t) { blocks[blocks.length - 1].lines.push(""); continue; }
    if (isHead(t)) {
      const kind = t.includes("KEY TAKEAWAYS") ? "takeaways" : WARN.test(t) ? "warning" : STEPS.test(t) ? "steps" : "topic";
      blocks.push({ head: t, kind, lines: [] });
    } else blocks[blocks.length - 1].lines.push(t);
  }
  return blocks.filter((b) => b.head || b.lines.some(Boolean));
};

const Lines = ({ lines, dot }) => {
  const out = [];
  let list = null;
  lines.forEach((t, i) => {
    const box = /^[□☐]\s*/.test(t);
    const bullet = box || /^[•·-]\s*/.test(t);
    const num = t.match(/^(\d+)\.\s+(.*)$/);
    if (bullet || num) {
      if (!list || list.box !== box) { list = { num: !!num, box, items: [] }; out.push(list); }
      list.items.push(num ? num[2] : t.replace(/^[•·□☐-]\s*/, ""));
    } else {
      list = null;
      if (t) out.push(<p key={`p${i}`} className="mb-3 text-[15px] leading-7 text-neutral-200">{t}</p>);
    }
  });
  return out.map((b, i) => (b.items ? (
    <ul key={`l${i}`} className="mb-3 space-y-2">
      {b.items.map((it, j) => (
        <li key={j} className="flex gap-3 text-[15px] leading-6 text-neutral-200">
          {b.num ? <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-extrabold text-black">{j + 1}</span>
            : b.box ? <Square size={16} className="mt-1 shrink-0 text-neutral-400" />
            : <span className={`mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />}
          <span>{it}</span>
        </li>
      ))}
    </ul>
  ) : b));
};

const STYLE = {
  takeaways: { box: "border-emerald-400 bg-emerald-400/10", head: "text-emerald-300", icon: CheckCircle2, dot: "bg-emerald-300" },
  warning: { box: "border-red-500/70 bg-red-500/10", head: "text-red-400", icon: AlertTriangle, dot: "bg-red-400" },
  steps: { box: "border-sky-400/60 bg-sky-400/5", head: "text-sky-300", icon: ListChecks, dot: "bg-sky-300" },
};

const Block = ({ b, i }) => {
  const st = STYLE[b.kind];
  if (!st) return (
    <div className="mb-2">
      {b.head && <h3 className="mb-2 mt-5 text-xs font-extrabold tracking-[2px] text-neutral-400">{b.head}</h3>}
      <Lines lines={b.lines} dot="bg-[var(--accent)]" />
    </div>
  );
  const Icon = st.icon;
  return (
    <div className={`my-5 rounded-xl border-l-4 p-4 ${st.box}`} data-testid={`lso-block-${b.kind}-${i}`}>
      <p className={`mb-3 flex items-center gap-2 text-xs font-extrabold tracking-[2px] ${st.head}`}><Icon size={16} />{b.head}</p>
      <Lines lines={b.lines} dot={st.dot} />
    </div>
  );
};

export const LessonSection = ({ s, i }) => (
  <section className="mb-10" data-testid={`lso-section-${i}`}>
    <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-[var(--accent)]"><ShieldAlert size={20} className="shrink-0" />{s.heading}</h2>
    {s.image_url && (
      <figure className="mx-auto mb-5 max-w-2xl overflow-hidden rounded-xl border border-white/10 bg-black" data-testid={`lso-section-image-${i}`}>
        <img src={s.image_url} alt={s.image_alt || s.heading} loading="lazy" className="aspect-[3/2] w-full object-cover" />
      </figure>
    )}
    {parseLesson(s.body).map((b, j) => <Block key={j} b={b} i={`${i}-${j}`} />)}
  </section>
);

export const ModuleProgress = ({ n, total }) => (
  <div className="mb-6" data-testid="lso-module-progress">
    <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-500" style={{ width: `${(n / Math.max(total, 1)) * 100}%` }} /></div>
  </div>
);
